import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { Logo } from "@/components/lembra/Logo";
import type { BlogPostWithRelations, BlogCategory } from "@/types/blog";

const PAGE_SIZE = 9;

const trackFilters = [
  { key: "all", label: "All posts" },
  { key: "office", label: "For Workplaces" },
  { key: "kitchen", label: "For Bakeries" },
] as const;

export default function Blog() {
  const [posts, setPosts] = useState<BlogPostWithRelations[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [activeTrack, setActiveTrack] = useState<"all" | "office" | "kitchen">("all");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    const load = async () => {
      const [postsRes, catsRes] = await Promise.all([
        supabase
          .from("blog_posts")
          .select("*, blog_authors(name, slug, avatar_url), blog_categories(name, slug)")
          .eq("status", "published")
          .lte("published_at", new Date().toISOString())
          .order("is_cornerstone", { ascending: false })
          .order("published_at", { ascending: false }),
        supabase
          .from("blog_categories")
          .select("*")
          .order("sort_order"),
      ]);
      // MS397: guard against silent blank-page on DB/network failure.
      if (postsRes.error || catsRes.error) {
        setFetchError(true);
        setLoading(false);
        toast.error("Couldn't load posts. Please refresh the page.");
        return;
      }
      if (postsRes.data) setPosts(postsRes.data as BlogPostWithRelations[]);
      if (catsRes.data) setCategories(catsRes.data as BlogCategory[]);
      setLoading(false);
    };
    load();
  }, []);

  // Filter posts by track and category
  const filtered = posts.filter((p) => {
    if (activeTrack !== "all" && p.track !== activeTrack && p.track !== "both") return false;
    if (activeCategory && p.category_id !== activeCategory) return false;
    return true;
  });

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Filter categories by active track
  const filteredCategories = activeTrack === "all"
    ? categories
    : categories.filter((c) => c.track === activeTrack || c.track === "both");

  const handleTrackChange = (track: "all" | "office" | "kitchen") => {
    setActiveTrack(track);
    setActiveCategory(null);
    setVisibleCount(PAGE_SIZE);
  };

  const handleCategoryChange = (catId: string | null) => {
    setActiveCategory(catId);
    setVisibleCount(PAGE_SIZE);
  };

  // MS182: structured data for the blog index — same shape as the
  // /vendors directory equivalent.
  //   1. BreadcrumbList — 2-rung "Lembra › Blog" to close the trail
  //      MS181 added on each individual post page.
  //   2. CollectionPage wrapping a Blog with an ItemList of every
  //      published post (linked by canonical URL). Emits only when
  //      the supabase load resolves so the prerender doesn't bake
  //      `numberOfItems: 0` on the cold-paint snapshot.
  const blogBreadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Lembra", item: "https://lembra.com.au" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://lembra.com.au/blog" },
    ],
  };

  const blogCollectionLd: Record<string, unknown> | null =
    posts.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "Blog",
          url: "https://lembra.com.au/blog",
          name: "Lembra Blog",
          description:
            "Workplace celebration tips, corporate catering guides, and team culture insights from Lembra.",
          blogPost: posts.slice(0, 50).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://lembra.com.au/blog/${p.slug}`,
            ...(p.published_at ? { datePublished: p.published_at } : {}),
            ...(p.updated_at ? { dateModified: p.updated_at } : {}),
          })),
        }
      : null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Blog | Lembra</title>
        <meta name="description" content="Workplace celebration tips, corporate catering guides, and team culture insights from Lembra." />
        <link rel="canonical" href="https://lembra.com.au/blog" />

        {/* Override App.tsx defaults with blog-index-specific copy.
            og:image stays as the brand banner; per-post pages override
            again from BlogSEOHead with their per-slug image. */}
        <meta property="og:title" content="Lembra Blog, workplace celebrations, sorted" />
        <meta property="og:description" content="Workplace celebration tips, corporate catering guides, and team culture insights from Lembra." />
        <meta property="og:url" content="https://lembra.com.au/blog" />
        <meta name="twitter:title" content="Lembra Blog, workplace celebrations, sorted" />
        <meta name="twitter:description" content="Workplace celebration tips, corporate catering guides, and team culture insights from Lembra." />

        {/* MS182 JSON-LD. blogPost capped at 50 to keep the payload
            reasonable even if the post count grows past 100. */}
        <script type="application/ld+json">{JSON.stringify(blogBreadcrumbLd)}</script>
        {blogCollectionLd && (
          <script type="application/ld+json">{JSON.stringify(blogCollectionLd)}</script>
        )}
      </Helmet>

      {/* Header */}
      <header className="py-6 px-6 md:px-10">
        <Logo size="md" />
      </header>

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <AnimatedReveal animation="fadeInUpSoft" duration={800}>
            <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight">
              Insights for workplaces that celebrate well
            </h1>
          </AnimatedReveal>
          <AnimatedReveal animation="fadeInUpSoft" duration={800} delay={150}>
            <p className="font-display italic text-lg md:text-xl text-charcoal/60 mt-4 max-w-xl mx-auto">
              Ideas and guides for HR teams, office managers, and bakery partners.
            </p>
          </AnimatedReveal>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-white border-b border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 py-4 space-y-3">
          {/* Track toggle */}
          <div className="flex items-center justify-center gap-2">
            {trackFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => handleTrackChange(f.key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  activeTrack === f.key
                    ? "bg-navy text-white"
                    : "bg-charcoal/5 text-charcoal/60 hover:bg-charcoal/10"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Category chips */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            <button
              onClick={() => handleCategoryChange(null)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                !activeCategory
                  ? "bg-coral text-white"
                  : "bg-charcoal/5 text-charcoal/60 hover:bg-charcoal/10"
              }`}
            >
              All
            </button>
            {filteredCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                  activeCategory === cat.id
                    ? "bg-coral text-white"
                    : "bg-charcoal/5 text-charcoal/60 hover:bg-charcoal/10"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Post Grid */}
      <section className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-charcoal/5 rounded-2xl aspect-[3/4] animate-shimmer" />
            ))}
          </div>
        ) : fetchError ? (
          <div className="text-center py-16">
            <p className="text-charcoal/50 text-lg">Something went wrong. Please try refreshing.</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-block mt-4 text-sm text-coral hover:underline"
            >
              Refresh page
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-charcoal/50 text-lg">No posts found.</p>
            <Link to="/" className="inline-block mt-4 text-sm text-coral hover:underline">
              ← Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {visible.map((post, i) => (
                <AnimatedReveal
                  key={post.id}
                  animation="fadeInUp"
                  duration={600}
                  delay={(i % 3) * 100}
                  threshold={0.1}
                >
                  <BlogPostCard post={post} />
                </AnimatedReveal>
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-12">
                <button
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="px-8 py-3 rounded-full border-2 border-navy/20 text-navy font-semibold text-sm hover:bg-navy hover:text-white transition-colors duration-200"
                >
                  Load more posts
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-charcoal/10 py-8 px-6 text-center">
        <Link to="/" className="text-sm text-charcoal/50 hover:text-coral transition-colors">
          ← Back to Lembra
        </Link>
      </footer>
    </div>
  );
}
